/**
 * GripArmedPrompt — N4 GRIPS-ARMED-STRETCH-I18N, mode-cycle-aware since the
 * GRIPS-INTERACTION PR-2 (origin/main) re-sync.
 *
 * RU/EN command-line echo for the hot-grip CLICK-ARM modal phase (GRIPS-
 * INTERACTION PR-1, `hotGripStore.ts`). AutoCAD parity: the instant a grip
 * click-arms, the command line shows the modal mode banner ("** STRETCH **")
 * and the destination prompt ("Specify stretch point:") until the second
 * click commits or the gesture aborts (Escape / any keydown / blur / click
 * outside the stage — see `StageGripDrag.ts`). PR-2 lets Enter/Space cycle
 * the armed grip's mode (Stretch → Move → Rotate → Scale → Mirror) — this
 * component reads the LIVE cycled mode via `getArmedEditModeSnapshot()`
 * (NOT a static "always stretch" assumption, which PR-1's original draft of
 * this file used before PR-2 landed the real per-grip mode tracking), so the
 * banner + point-prompt text updates on every cycle right alongside PR-2's
 * own `useGripModeEcho` single-line status-strip echo.
 *
 * Self-contained (zero props): subscribes to the SAME module-level
 * `hotGripStore` external store `Scene3DStage.tsx` already reads for the
 * armed cursor (`useSyncExternalStore(subscribeHotGrips, ...)`), so this is
 * a second independent subscriber to the one authoritative armed-grip state
 * — not a parallel tracking mechanism. Renders `null` while idle. Mirrors
 * `RefeditToolbar.tsx`'s no-props / own-store-subscription shape.
 */

import { type ReactElement, useSyncExternalStore } from "react";

import { useT } from "../../i18n";
import { GRIP_ARMED_PROMPT_KEYS } from "./gripInteractionConstants";
import {
  getArmedEditModeSnapshot,
  getArmedGripSnapshot,
  subscribeHotGrips,
} from "./hotGripStore";

export function GripArmedPrompt(): ReactElement | null {
  const t = useT();
  const armedGrip = useSyncExternalStore(
    subscribeHotGrips,
    getArmedGripSnapshot,
    getArmedGripSnapshot,
  );
  const armedMode = useSyncExternalStore(
    subscribeHotGrips,
    getArmedEditModeSnapshot,
    getArmedEditModeSnapshot,
  );

  if (armedGrip === null) return null;

  const keys = GRIP_ARMED_PROMPT_KEYS[armedMode];

  return (
    <div
      className="command-grip-armed-prompt"
      data-testid="command-grip-armed-prompt"
      role="status"
      aria-live="polite"
    >
      <div className="command-grip-armed-prompt-mode">{t(keys.mode)}</div>
      <div className="command-grip-armed-prompt-point">{t(keys.point)}</div>
    </div>
  );
}
